import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { SharedModule } from "@shared/shared.module";
import { environment } from "@env/environment";

const analysesUrl = `${environment.apiBaseUrl}platform/gitwiser/analyses`;
const gitwiserUrl = `${environment.apiBaseUrl}platform/gitwiser`;
const pullAnalyticsUrl = `${environment.apiBaseUrl}platform/pull-analytics`;
const deploymentGitwiserUrl = `${environment.apiBaseUrl}platform/gitwiser/deployment-analytics`;
const processUrl = `${environment.apiBaseUrl}platform/pull-analytics-process`;
const activity = `${environment.apiBaseUrl}platform/activities`;

const headers = new HttpHeaders({
  'x-no-notification': 'true'
});

@Injectable({
  providedIn: SharedModule
})
export class GitwiserService {

  constructor(private http: HttpClient) { }

  list(
    {
      sortKey,
      sortValue,
      filter,
      filterTeams,
      page,
      searchKey
    }: {
      sortKey?: string,
      sortValue?: string,
      filterTeams?: string,
      filter?: string,
      page?: string,
      searchKey?: string
    }): Observable<Gitwiser.GitwiserModel> {
    let params = new HttpParams();

    if (sortKey) {
      params = params.append('sortKey', sortKey);
    }

    if (sortValue) {
      params = params.append('sortValue', sortValue);
    }

    if (filter) {
      params = params.append('filter', filter);
    }

    if (filterTeams) {
      params = params.append('filterTeams', filterTeams);
    }

    if (page) {
      params = params.append('page', page);
    }

    if (searchKey) {
      params = params.append('searchKey', searchKey);
    }


    return this.http.get<Gitwiser.GitwiserModel>(`${analysesUrl}`, { params });
  }

  teamList(
    { sortKey, sortValue, filter, filterTeams, page, searchKey }:
      { sortKey?: string, sortValue?: string, filter?: string, page?: string,
      searchKey?: string, filterTeams?: string, }):
    Observable<any> { let params = new HttpParams();

    if (sortKey) {
      params = params.append('sortKey', sortKey);
    }

    if (sortValue) {
      params = params.append('sortValue', sortValue);
    }

    if (filter) {
      params = params.append('filter', filter);
    }

    if (page) {
      params = params.append('page', page);
    }

    if (searchKey) {
      params = params.append('searchKey', searchKey);
    }

    if (filterTeams) {
      params = params.append('filterTeams', filterTeams);
    }

    return this.http.get<any>(`${analysesUrl}/teams`, { params });
  }

  states(body: any): Observable<Map<string, string>> {
    return this.http.post<Map<string, string>>(`${analysesUrl}/states`, body, { headers });
  }

  get(id: string): Observable<GitwiserDetail.GitwiserDetailModel> {
    return this.http.get<GitwiserDetail.GitwiserDetailModel>(`${analysesUrl}/${id}`);
  }

  updateAnalysis(id: string, data: GitwiserDetail.AnalysisSettings): Observable<any> {
    return this.http.put<any>(`${analysesUrl}/${id}`, data);
  }

  getAnalysisSettings(id: string): Observable<GitwiserDetail.AnalysisSettings> {
    return this.http.get<GitwiserDetail.AnalysisSettings>(`${analysesUrl}/${id}/settings`);
  }

  create(analysis: any, scopeId: string): Observable<Gitwiser.GitwiserModel> {
    let params = new HttpParams();
    if (scopeId) {
      params = params.append('scope', scopeId);
    }
    return this.http.post<Gitwiser.GitwiserModel>(`${analysesUrl}`, analysis, { params });
  }

  delete(id: string): Observable<any> {
    return this.http.delete<any>(`${analysesUrl}/${id}`);
  }

  getReanalyse(id: string): Observable<any> {
    return this.http.get<any>(`${analysesUrl}/${id}/reanalyse`);
  }

  pullRequestAnalysisDelete(analysisId: string): Observable<any> {
    let params = new HttpParams();
    if (analysisId) {
      params = params.append('analysisId', analysisId);
    }

    return this.http.delete<any>(`${processUrl}/process`, { params });
  }

  cancel(id: string): Observable<any> {
    return this.http.post<any>(`${analysesUrl}/${id}/stop`, {}, { headers });
  }

  cancelRunning(id: string): Observable<any> {
    return this.http.post<any>(`${analysesUrl}/${id}/cancel`, {}, { headers });
  }

  sync(id: string): Observable<any> {
    return this.http.post<any>(`${analysesUrl}/${id}/sync`, {}, { headers });
  }

  fields(id: string, scopeId?: string): Observable<any> {
    let params = new HttpParams();
    if (scopeId) {
      params = params.append('scope', scopeId);
    }
    return this.http.get<any>(`${gitwiserUrl}/addons/${id}/analyzer-templates`, { params });
  }

  deploymentAnalyticsFields(id: string,steps:boolean): Observable<any> {
    let params = new HttpParams();
    params = params.append('steps', steps);

    return this.http.get<any>(`${gitwiserUrl}/addons/${id}/deployment-analytics-templates`, { params });
  }

  deploymentAnalyticsApmFields(id: string): Observable<any> {
    const params = new HttpParams();
    return this.http.get<any>(`${gitwiserUrl}/addons/${id}/deployment-analytics-incident-source-templates`, { params });
  }

  getAddons(): Observable<Gitwiser.AddonModel[]> {
    return this.http.get<Gitwiser.AddonModel[]>(`${gitwiserUrl}/addons`);
  }

  createExcludeCommit(id: string, commitId: string, formData: any): Observable<any> {
    return this.http.put<any>(`${analysesUrl}/${id}/commits/${commitId}/exclude`, formData);
  }

  getExcludedCommits(id: string, commitId: string, formData: any): Observable<any> {
    return this.http.put<any>(`${analysesUrl}/${id}/commits/${commitId}/exclude`, formData);
  }

  getResult(analysisId: string, type: string, date?: any, teamIds?: any): Observable<any> {
    let params = new HttpParams();

    if (date && date.fromDate) {
      params = params.append('fromDate', date.fromDate);
      params = params.append('toDate', date.toDate);
    }
    if (date && !date.fromDate) {
      params = params.append('fromDate', date);
    }
    if (teamIds) {
      params = params.append('teamIds', teamIds);
    }

    return this.http.get<any>(`${analysesUrl}/${analysisId}/results?type=${type}`, { params });
  }

  getResultCommit(analysisId: string, type: string, date?: any ,teamIds?: string, page?: any, searchByValues?: any, pageSize? :any, sorting? : any): Observable<any> {
    const body = {
      analysisId,
      teamIds,
      gitWiserWidgetTypeEnum: type,
      page,
      pageSize,
      searchByValues,
      sorting,
    };

    let params = new HttpParams();

    if (date && date.fromDate) {
      params = params.append('fromDate', date.fromDate);
      params = params.append('toDate', date.toDate);
    }

    if (date && !date.fromDate) {
      params = params.append('fromDate', date);
    }

    return this.http.post<any>(`${analysesUrl}/${analysisId}/results-with-page?type=${type}`, body, {headers, params});
  }

  getResultContributorData(
    analysisId: string,
    type: string,
    date?: any,
    teamIds?: string,
    page?: any,
    searchByValues?: { [key: string]: string[] },
    pageSize?: any,
    sorting?: any
  ): Observable<any> {
    const body: any = {
      analysisId,
      teamIds,
      gitWiserWidgetTypeEnum: type,
      page,
      pageSize,
      sorting
    };

    if (searchByValues && Object.keys(searchByValues).length > 0) {
      body.searchByValues = searchByValues;
    }
    let params = new HttpParams();

    if (date && date.fromDate) {
      params = params.append('fromDate', date.fromDate);
      params = params.append('toDate', date.toDate);
    }

    if (date && !date.fromDate) {
      params = params.append('fromDate', date);
    }

    return this.http.post<any>(`${analysesUrl}/${analysisId}/widget-data?type=${type}`, body, { headers, params });
  }


  getPullAnalyticsResult(analysisId: string, widgetCode: string, date?: any, goal?, page?: number, teamIds?: any, searchByValues?: any,sortedBy?: any, sortedOrder?: any): Observable<any> {

    const body = {
      page,
      searchByValues,
      sortedBy,
      sortedOrder,
    };

    let params = new HttpParams();

    if (date && date.fromDate) {
      params = params.append('fromDate', date.fromDate);
      params = params.append('toDate', date.toDate);
    }
    if (date && !date.fromDate) {
      params = params.append('fromDate', date);
    }

    if (widgetCode) {
      params = params.append('widgetCode', widgetCode);
    }

    if (analysisId) {
      params = params.append('analysisId', analysisId);
    }

    if (goal) {
      params = params.append('goal', goal);
    }

    if (teamIds) {
      params = params.append('teamIds', teamIds);
    }

    return this.http.post<any>(`${pullAnalyticsUrl}/widget`, body, {headers, params });
  }

  getContributors(): Observable<Gitwiser.Contributors> {
    return this.http.get<Gitwiser.Contributors>(`${analysesUrl}/contributors`);
  }

  getProcess(analysisId: string): Observable<any> {
    let params = new HttpParams();

    if (analysisId) {
      params = params.append('analysisId', analysisId);
    }

    return this.http.get<any>(`${processUrl}/process`, { params });
  }

  startAnalysis(analysisId: string): Observable<any> {
    let params = new HttpParams();

    if (analysisId) {
      params = params.append('analysisId', analysisId);
    }

    return this.http.post<any>(`${processUrl}/process`, {}, { params });
  }

  getSettings(analysisId: string): Observable<any> {
    let params = new HttpParams();

    if (analysisId) {
      params = params.append('analysisId', analysisId);
    }
    return this.http.get<any>(`${gitwiserUrl}/pull-analytics/settings`, { params });
  }

  getGoalSettings(widgetCode: string, analysisId: string): Observable<any> {
    let params = new HttpParams();

    if (widgetCode) {
      params = params.append('widgetCode', widgetCode);
    }

    if (analysisId) {
      params = params.append('analysisId', analysisId);
    }

    return this.http.get<any>(`${pullAnalyticsUrl}/config`, { params });
  }

  saveGoalSettings(data): Observable<any> {
    return this.http.post<any>(`${pullAnalyticsUrl}/config`, data);
  }

  updateGoalSettings(data): Observable<any> {
    return this.http.put<any>(`${pullAnalyticsUrl}/config`, data);
  }

  getWidgetType(addon?: string): Observable<any> {
    let params = new HttpParams();

    if (addon) {
      params = params.append('addon', addon);
    }
    return this.http.get<any>(`${pullAnalyticsUrl}/widgets`, { params });
  }

  startReAnalysePra(analysisId: string): Observable<any> {
    let params = new HttpParams();

    if (analysisId) {
      params = params.append('analysisId', analysisId);
    }
    return this.http.post<any>(`${processUrl}/reanalyse`, {}, { params });
  }

  getAddonsDeployment(): Observable<any> {
    return this.http.get<any>(`${deploymentGitwiserUrl}/addons`);
  }

  startDeploymentAnalysis(analysisId: string, requestData: any): Observable<any> {
    const deploymentAnalyticsParams = {};
    if (requestData?.formData && typeof requestData.formData === 'object') {
      Object.keys(requestData.formData).forEach((key) => {
        if (requestData.formData[key]?.key != null) {
          deploymentAnalyticsParams[key] = requestData.formData[key].key;
        }
      });
    } else {
      console.error('formData is null or not an object');
    }
    requestData['analysisId'] = analysisId;
    requestData['deploymentAnalyticsPatterns'] = requestData.deploymentAnalyticsPatterns;
    requestData['deploymentDatasourceId'] = requestData.formData?.deploymentAnalyticsDatasources?.key;
    requestData['deploymentAnalyticsParams'] = deploymentAnalyticsParams;
    delete requestData.formData;
    return this.http.post<any>(`${deploymentGitwiserUrl}/process`, requestData);
  }

  getDeploymentProcess(deploymentAnalyticsId: string): Observable<any> {
    let params = new HttpParams();

    if (deploymentAnalyticsId) {
      params = params.append('deploymentAnalyticsId', deploymentAnalyticsId);
    }

    return this.http.get<any>(`${deploymentGitwiserUrl}/process`, { params });
  }

  getDeploymentWidgetType(): Observable<any> {
    return this.http.get<any>(`${deploymentGitwiserUrl}/widgets`);
  }

  getDeploymentAnalytics(analysisId: string): Observable<any> {
    return this.http.get<any>(`${analysesUrl}/${analysisId}/deployment-analytics`);
  }

  getDeploymentAnalyticsResult(analysisId: string, type: string, date?: any, page?: any, title?: any, author?: any, status?: any, assignee?: any, sortField?: any, reversed?: any,selectedAnalysisIds?: any,pipelineName?: any): Observable<any> {
  let params = new HttpParams();

    if (date && date.fromDate) {
      params = params.append('fromDate', date.fromDate);
      params = params.append('toDate', date.toDate);
    }
    if (date && !date.fromDate) {
      params = params.append('fromDate', date);
    }

    if (type) {
      params = params.append('type', type);
    }

    if (page) {
      params = params.append('page', page);
    }

    if (title) {
      params = params.append('title', title);
    }

    if (author) {
      params = params.append('author', author);
    }

    if (status) {
      params = params.append('status', status);
    }

    if (assignee) {
      params = params.append('assignee', assignee);
    }

    if (sortField) {
      params = params.append('sortField', sortField);
    }

    if (reversed) {
      params = params.append('reversed', reversed);
    }

    if (selectedAnalysisIds) {
      params = params.append('ids', selectedAnalysisIds);
    }


    if (pipelineName) {
      params = params.append('pipelineNames', pipelineName);
    }

    return this.http.get<any>(`${deploymentGitwiserUrl}/${analysisId}/results`, { params });
  }

  startReAnalyseDeploymentAnalytics(analysisId: string): Observable<any> {
    let params = new HttpParams();

    if (analysisId) {
      params = params.append('analysisId', analysisId);
    }
    return this.http.post<any>(`${deploymentGitwiserUrl}/reanalyse`, {}, { params });
  }

  deploymentAnalyticsAnalysisDelete(analysisId: string): Observable<any> {
    let params = new HttpParams();
    if (analysisId) {
      params = params.append('analysisId', analysisId);
    }

    return this.http.delete<any>(`${deploymentGitwiserUrl}/process`, { params });
  }

  deploymentAnalyticsAnalysis(id: string): Observable<any> {
    let params = new HttpParams();
    if (id) {
      params = params.append('id', id);
    }

    return this.http.delete<any>(`${deploymentGitwiserUrl}`, { params });
  }

  getRunningAnalytics(): Observable<any> {
      return this.http.get<any>(`${analysesUrl}/running-analytics`);
  }

  updateDeploymentAnalyticsProcess(analysisId: string,requestData: any): Observable<any> {
    const deploymentAnalyticsParams = {};
    if (requestData?.formData && typeof requestData.formData === 'object') {
      Object.keys(requestData.formData).forEach((key) => {
        if (requestData.formData[key]?.key != null) {
          deploymentAnalyticsParams[key] = requestData.formData[key].key;
        }
      });
    } else {
      console.error('formData is null or not an object');
    }
    requestData['analysisId'] = analysisId;
    requestData['deploymentAnalyticsPatterns'] = requestData?.deploymentAnalyticsPatterns;
    requestData['deploymentDatasourceId'] = requestData.formData?.deploymentAnalyticsDatasources?.key;
    requestData['deploymentAnalyticsParams'] = deploymentAnalyticsParams;
    delete requestData.formData;
    return this.http.put<any>(`${deploymentGitwiserUrl}/process`, requestData)
  }

  deploymentDuringStatusChange(formData: any): Observable<any> {
    return this.http.put<any>(`${deploymentGitwiserUrl}/deployments/status`, formData);
  }

  getDeploymentList(analysisId: any, from:any): Observable<any> {
    let params = new HttpParams();

    if (from) {
      params = params.append('from', from);
    }
    return this.http.get<any>(`${deploymentGitwiserUrl}/${analysisId}/deployments`, { params });

  }


  getActivities(analysisId: any): Observable<any> {
    return this.http.get<any>(`${activity}/gitwiser/${analysisId}`);

  }

  getSummaryDetail(): Observable<any> {
    return this.http.get<any>(`${gitwiserUrl}/analyses/summary-detail`);

  }

  analysisSteps(addonId: any, scopeId:any): Observable<any> {
    let params = new HttpParams();
    params = params.append('addonId', addonId);
    params = params.append('scopeItemId', scopeId);
    return this.http.get<any>(`${gitwiserUrl}/addons/initialize-step`,{ params });
  }

  getFieldUrl(url: any,isIncludeDeployment: any): Observable<any> {
    let params = new HttpParams();
    params = params.append('includeDeployment', isIncludeDeployment);
    return this.http.get<any>(`${environment.apiBaseUrl}`+url,{ params });
  }

  excludePR(payload: any): Observable<any> {
    return this.http.post<any>(`${pullAnalyticsUrl}/exclude`,payload);
  }

  autoDoraAnalysisCreation(id:string){
    return this.http.put<any>(`${gitwiserUrl}/sync-deployment-analytics`,{analysisId:id} );
  }
}

